// Time windows: the one shared implementation.
//
// Need windows, event windows and availability windows all interact through this
// module. Three subtly different implementations is how coverage lies, so there
// is exactly one — and matching, coverage, ICS export and print all read it.

import type { Instant, TimeZoneId } from "./types.js";
import { endOfLocalDayExclusive, localDateOf, type LocalDate } from "./local-date.js";

const MINUTE_MS = 60_000;

/**
 * A half-open interval on the timeline: `[start, end)`.
 *
 * **Half-open is a decision, not an accident.** Back-to-back windows —
 * 09:00–12:00 and 12:00–15:00 — must not count as overlapping, or every helper
 * with two consecutive commitments would look double-booked. Closed intervals
 * would collide at the shared instant; half-open ones simply meet.
 */
export interface TimeWindow {
  readonly startsAt: Instant;
  readonly endsAt: Instant;
}

/** Why a proposed window is not acceptable. Reason codes; copy lives in product-language. */
export type WindowRejection =
  /** `endsAt` is at or before `startsAt`. A window with no duration helps nobody. */
  | { readonly kind: "empty_window"; readonly startsAt: Instant; readonly endsAt: Instant }
  /**
   * The window crosses a local day boundary. **Rejected in V1, never split**
   * (AVL-007, plan §11.6 "overnight prevention in V1").
   */
  | {
      readonly kind: "crosses_day_boundary";
      readonly startsAt: Instant;
      readonly endsAt: Instant;
      readonly startDate: LocalDate;
      readonly dayEndsAt: Instant;
    };

export type WindowValidation =
  | { readonly kind: "ok"; readonly window: TimeWindow }
  | { readonly kind: "rejected"; readonly rejection: WindowRejection };

/**
 * Validate a proposed window against the V1 rules.
 *
 * ## AVL-007: overnight windows are REJECTED, never split
 *
 * V1 refuses any window that crosses a local day boundary in the circle's zone.
 * The plan is explicit that this is *prevention*, not accommodation: the window
 * is not silently cut into two, and the person is asked to enter something
 * unambiguous instead.
 *
 * The payoff is that every downstream consumer — coverage, matching, ICS export,
 * print — gets to assume a window lives inside one calendar day. Midnight
 * crossing is the kind of edge case that produces a plausible-looking wrong
 * answer, and V1 buys its way out of the entire class.
 *
 * A window ending EXACTLY at the next local midnight is accepted. Because the
 * interval is half-open it contains none of the following day, so it has not
 * crossed anything — it has arrived at the boundary. Rejecting it would forbid
 * "the rest of today", which is a reasonable thing to offer.
 */
export function validateWindow(window: TimeWindow, zone: TimeZoneId): WindowValidation {
  if (window.endsAt <= window.startsAt) {
    return {
      kind: "rejected",
      rejection: {
        kind: "empty_window",
        startsAt: window.startsAt,
        endsAt: window.endsAt,
      },
    };
  }

  const startDate = localDateOf(window.startsAt, zone);
  const dayEndsAt = endOfLocalDayExclusive(startDate, zone);

  if (window.endsAt > dayEndsAt) {
    return {
      kind: "rejected",
      rejection: {
        kind: "crosses_day_boundary",
        startsAt: window.startsAt,
        endsAt: window.endsAt,
        startDate,
        dayEndsAt,
      },
    };
  }

  return { kind: "ok", window };
}

/** Convenience predicate over {@link validateWindow}. */
export function isValidWindow(window: TimeWindow, zone: TimeZoneId): boolean {
  return validateWindow(window, zone).kind === "ok";
}

/**
 * Does `outer` cover the WHOLE of `inner`?
 *
 * ## This is the predicate matching uses — and the only one
 *
 * AVL-021: "Matching MUST require full containment of the need window."
 * A helper is offered as *Could help for the full time* only when their
 * availability contains the entire need window (plan §11.9). Someone available
 * 09:00–11:00 is **not** a match for a need running 09:15–13:00, however much of
 * it they cover.
 *
 * There is deliberately no overlap-scoring, no ranking by percentage covered,
 * and no "best match" anywhere in this codebase (AVL-025, AVL-026). Partial
 * overlap may be shown to an organiser as context, but it is never a match and
 * never a number.
 *
 * Boundaries are inclusive of the shared instants: a window contains itself.
 */
export function windowContains(outer: TimeWindow, inner: TimeWindow): boolean {
  return outer.startsAt <= inner.startsAt && inner.endsAt <= outer.endsAt;
}

/**
 * Do these two windows share any time at all?
 *
 * **Not for matching.** This exists for CONFLICT detection — "does this person
 * already have an accepted commitment clashing with this one?" (AVL-023,
 * COM-005, invariant 8). Using it to decide a match would silently reintroduce
 * the overlap semantics AVL-021 replaced; use {@link windowContains} for that.
 *
 * Half-open, so touching windows do not overlap: 09:00–12:00 and 12:00–15:00
 * are back-to-back, not a clash.
 */
export function windowsOverlap(a: TimeWindow, b: TimeWindow): boolean {
  return a.startsAt < b.endsAt && b.startsAt < a.endsAt;
}

/**
 * The overlapping portion, or `null` when they do not overlap.
 *
 * Provided for explaining a conflict to a person — "these clash between 10:00
 * and 11:00" — never for scoring a match.
 */
export function windowIntersection(a: TimeWindow, b: TimeWindow): TimeWindow | null {
  if (!windowsOverlap(a, b)) return null;
  return {
    startsAt: a.startsAt > b.startsAt ? a.startsAt : b.startsAt,
    endsAt: a.endsAt < b.endsAt ? a.endsAt : b.endsAt,
  };
}

/**
 * Do these windows meet or overlap once a buffer is allowed around each?
 *
 * AVL-024: matching applies a configured transition buffer — travel time between
 * one responsibility and the next. Separate from {@link windowsOverlap} because
 * "these clash" and "these are too tight to be realistic" are different things
 * to tell somebody (plan §11.9: *Timing is tight*).
 */
export function windowsConflictWithBuffer(
  a: TimeWindow,
  b: TimeWindow,
  bufferMinutes: number,
): boolean {
  if (bufferMinutes < 0) {
    throw new RangeError(`Transition buffer must not be negative, got ${String(bufferMinutes)}`);
  }
  const buffer = bufferMinutes * MINUTE_MS;
  return a.startsAt - buffer < b.endsAt && b.startsAt - buffer < a.endsAt;
}

/**
 * How long the window lasts, in real elapsed minutes.
 *
 * Derived from the instants, so it is automatically correct across a DST
 * transition: a window from 00:30 to 03:30 on the spring-forward day is two
 * hours of somebody's life, not three, and this returns 120.
 */
export function windowDurationMinutes(window: TimeWindow): number {
  return (window.endsAt - window.startsAt) / MINUTE_MS;
}

/** Is this instant inside the window? Half-open: `start` is in, `end` is not. */
export function windowIncludes(window: TimeWindow, at: Instant): boolean {
  return window.startsAt <= at && at < window.endsAt;
}
